/**
 * Quotas 頁面
 * 配額管理
 */

import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { get, patch, post } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import toast from 'react-hot-toast';

/**
 * 租戶列表回應類型
 */
interface TenantListResponse {
  success: boolean;
  data: {
    tenants: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
  };
}

/**
 * 配額回應類型
 */
interface QuotaResponse {
  success: boolean;
  data: {
    tenantId: string;
    quotaOverrides: Record<string, number | boolean> | null;
    planQuotas: Record<string, number | boolean> | null;
    planName: string | null;
    effectiveQuotas: Record<string, number | boolean>;
  };
}

/**
 * Quotas 頁面
 */
export function Quotas() {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [quotaOverrides, setQuotaOverrides] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // 取得租戶列表
  const { data: tenantsData, isLoading: tenantsLoading } =
    useSWR<TenantListResponse>(
      '/api/super-admin/tenants?page=1&limit=100',
      get as any
    );

  // 取得配額（當選擇租戶時）
  const {
    data: quotaData,
    error: quotaError,
    isLoading: quotaLoading,
    mutate,
  } = useSWR<QuotaResponse>(
    selectedTenantId
      ? `/api/super-admin/tenants/${selectedTenantId}/quota`
      : null,
    get as any
  );

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setIsEditing(false);
    setQuotaOverrides('');
  };

  const handleEdit = () => {
    if (quotaData?.data) {
      setQuotaOverrides(
        JSON.stringify(quotaData.data.quotaOverrides || {}, null, 2)
      );
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (quotaData?.data) {
      setQuotaOverrides(
        JSON.stringify(quotaData.data.quotaOverrides || {}, null, 2)
      );
    }
  };

  const handleSave = async () => {
    if (!selectedTenantId) return;

    setIsSubmitting(true);
    try {
      const parsed = quotaOverrides ? JSON.parse(quotaOverrides) : {};
      await patch(`/api/super-admin/tenants/${selectedTenantId}/quota`, {
        quotaOverrides: parsed,
      });
      toast.success('配額更新成功');
      setIsEditing(false);
      mutate();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('JSON 格式錯誤，請檢查輸入');
      } else {
        toast.error(error instanceof Error ? error.message : '更新失敗');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (!selectedTenantId) return;

    setIsResetting(true);
    try {
      await post(`/api/super-admin/tenants/${selectedTenantId}/quota/reset`);
      toast.success('配額已重置為方案預設值');
      setShowResetConfirm(false);
      mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '重置失敗');
    } finally {
      setIsResetting(false);
    }
  };

  // 當配額數據載入時，更新編輯框
  useEffect(() => {
    if (quotaData?.data && !isEditing) {
      setQuotaOverrides(
        JSON.stringify(quotaData.data.quotaOverrides || {}, null, 2)
      );
    }
  }, [quotaData, isEditing]);

  return (
    <Layout title="配額管理">
      <div className="space-y-4">
        {/* 租戶選擇 */}
        <div className="bg-white rounded-lg shadow p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            選擇租戶
          </label>
          {tenantsLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <select
              value={selectedTenantId}
              onChange={e => handleTenantChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">請選擇租戶</option>
              {tenantsData?.data?.tenants?.map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.slug})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 配額顯示 */}
        {selectedTenantId && (
          <div className="space-y-4">
            {quotaLoading ? (
              <div className="flex items-center justify-center py-8 bg-white rounded-lg shadow">
                <LoadingSpinner size="lg" />
              </div>
            ) : quotaError ? (
              <ErrorDisplay error={quotaError} onRetry={() => mutate()} />
            ) : quotaData?.data ? (
              <>
                {/* 配額資訊卡片 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      方案
                    </h3>
                    <p className="text-2xl font-bold text-gray-900">
                      {quotaData.data.planName || '無方案'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      租戶覆蓋
                    </h3>
                    <p className="text-sm text-gray-900">
                      {quotaData.data.quotaOverrides
                        ? `${Object.keys(quotaData.data.quotaOverrides).length} 項覆蓋`
                        : '無覆蓋'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      有效配額
                    </h3>
                    <p className="text-sm text-gray-900">
                      {Object.keys(quotaData.data.effectiveQuotas).length}{' '}
                      項配額
                    </p>
                  </div>
                </div>

                {/* 配額詳情 */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      配額詳情
                    </h3>
                    {!isEditing && (
                      <div className="space-x-2">
                        <button
                          onClick={handleEdit}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          編輯
                        </button>
                        <button
                          onClick={() => setShowResetConfirm(true)}
                          className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                          重置為方案預設值
                        </button>
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          配額覆蓋 (JSON)
                        </label>
                        <textarea
                          value={quotaOverrides}
                          onChange={e => setQuotaOverrides(e.target.value)}
                          rows={12}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                          placeholder='{"max_users": 100, "max_products": 500}'
                        />
                      </div>
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={handleCancel}
                          disabled={isSubmitting}
                          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          取消
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={isSubmitting}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          {isSubmitting ? '儲存中...' : '儲存'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* 有效配額 */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          有效配額（最終值）
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <pre className="text-sm text-gray-900 overflow-x-auto">
                            {JSON.stringify(
                              quotaData.data.effectiveQuotas,
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      </div>

                      {/* 租戶覆蓋 */}
                      {quotaData.data.quotaOverrides && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            租戶覆蓋
                          </h4>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <pre className="text-sm text-gray-900 overflow-x-auto">
                              {JSON.stringify(
                                quotaData.data.quotaOverrides,
                                null,
                                2
                              )}
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* 方案配額 */}
                      {quotaData.data.planQuotas && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            方案配額
                          </h4>
                          <div className="bg-green-50 rounded-lg p-4">
                            <pre className="text-sm text-gray-900 overflow-x-auto">
                              {JSON.stringify(
                                quotaData.data.planQuotas,
                                null,
                                2
                              )}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* 重置確認對話框 */}
        <ConfirmDialog
          isOpen={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          onConfirm={handleReset}
          title="重置配額"
          message="確定要將此租戶的配額重置為方案預設值嗎？所有租戶覆蓋將被清除。"
          confirmText="重置"
          variant="warning"
          isLoading={isResetting}
        />
      </div>
    </Layout>
  );
}
